// HostCard — the designer's agate Fleet host card (capacity summary + a per-host
// sandbox table), wired to REAL signals: enforcing is reflected (never a hardcoded
// green tag), metrics are the host's actual vCPU/mem, the tier links through to its
// policy (read-only — no fake role-select), Connected distinguishes an attached
// developer from a merely-existing sandbox, and Stop/Destroy key on the principal.
import React from "react";
import { Avatar, Badge, Collapse, Divider, Dropdown, Modal, Popover, Progress, Space, Typography, theme } from "antd";
import type { TableColumnsType } from "antd";
import { Wifi, WifiOff, Clock, MoreVertical, SquareDashed, Square, IdCard, Trash2, KeyRound } from "lucide-react";
import { CustomButton, CustomCard, CustomTable, Tag } from "@/shared/ui";
import { PolicyChip } from "@/components/fleet/PolicyChip";
import type { Host, Sandbox } from "../model/fleetTypes";

const { Text, Title } = Typography;
const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";
const SANS = "var(--font-sans, 'DM Sans Variable', 'DM Sans', ui-sans-serif, system-ui, -apple-system, sans-serif)";

const STATUS_TAG_COLOR: Record<Sandbox["status"], string> = {
  running: "success",
  idle: "default",
  stopped: "default",
};

// PolicyChip now lives in @/components/fleet/PolicyChip — shared with the
// Deploy SSH sandbox modal's grant list, which used to have its own
// separate, plainer tier tag.

export interface HostCardProps {
  host: Host;
  sandboxes: Sandbox[];
  onAction: (principal: string, verb: "stop" | "destroy") => void;
  /** Opens the resize-host modal (useFleetOps.resizeHost). FleetPage only
   *  ever renders HostCard for worker hosts — the control-plane host gets
   *  ManagementHostCard instead — so every action below always applies. */
  onResize: (name: string, current?: string) => void;
  /** Starts the decommission-host job (useFleetOps.removeHost — kept its
   *  original name; only the user-facing "Decommission" label changed). */
  onRemove: (name: string) => void;
  /** Opens the shared ConnectModal scoped to this host (FleetPage builds the
   *  ConnectHost payload from the raw host record and sets it as focusHost).
   *  Per-host, not page-level: Dev host is a required, host-specific address
   *  (self-hosted, never auto-discovered), and since a host enforces exactly
   *  one policy, which host the admin hands out as Dev host is what actually
   *  determines the user's policy. */
  onConnect: () => void;
  /** Opens ProvisionSshSandboxModal pre-scoped (host field locked) to this
   *  host — the per-host counterpart to the page-level "Deploy SSH sandbox"
   *  CTA. */
  onDeploySshSandbox: () => void;
  /** Opens ManageSshAccessModal scoped to this host — reviewing/revoking
   *  existing grants, split out from onDeploySshSandbox's add-only flow
   *  (2026-09-10). Lives in the kebab, not a header CTA: rarer and more
   *  administrative than granting access, same reasoning as Resize/
   *  Decommission staying there. */
  onManageSshAccess: () => void;
  /** The fleet's size catalog's friendly name for this host ("Small" /
   *  "Medium" / "Large"), resolved by FleetPage matching host.vcpu/memGiB
   *  against /fleet/sizes — the same vocabulary the Add-host/Resize size
   *  picker uses. Undefined when the catalog doesn't have a matching entry
   *  (or hasn't loaded yet); falls back to the raw instance type below. */
  sizeTier?: string;
}

export const HostCard: React.FC<HostCardProps> = ({ host, sandboxes, onAction, onResize, onRemove, onConnect, onDeploySshSandbox, onManageSshAccess, sizeTier }) => {
  const { token } = theme.useToken();
  const sandboxCount = sandboxes.length;

  // Decommissioning a host deletes its VM (and its disks). Refuse while ANY
  // sandbox is on it — they must be destroyed first. This mirrors fleetctl's
  // own refusal (it blocks removal on any sandbox, running or stopped), so
  // the UI never green-lights a decommission the backend job would reject.
  // Moving stopped sandboxes between hosts is a later enhancement.
  const handleRemove = () => {
    if (sandboxCount > 0) {
      Modal.warning({
        title: `${host.name} has ${sandboxCount} sandbox${sandboxCount === 1 ? "" : "es"}`,
        content: "Destroy the sandboxes on this host before decommissioning it. (Moving stopped sandboxes to another host is coming later.)",
        okText: "Got it",
      });
      return;
    }
    Modal.confirm({
      title: `Decommission ${host.name}?`,
      content: "This terminates the host. This cannot be undone.",
      okText: "Decommission",
      okButtonProps: { danger: true },
      onOk: () => onRemove(host.name),
    });
  };

  const meter = (pct: number) =>
    pct >= 90 ? token.colorError : pct >= 70 ? token.colorWarning : token.colorPrimary;

  const columns: TableColumnsType<Sandbox> = [
    {
      title: "Owner",
      key: "owner",
      // Required now that the table uses tableLayout="fixed" below — unlike
      // auto layout, fixed layout doesn't size a width-less column to its
      // content; it collapses instead, and the content (avatar + name/email)
      // overflowed visibly into the Policy column next to it rather than
      // wrapping or clipping.
      width: 230,
      render: (_: unknown, s: Sandbox) => (
        <Space size={8} align="center">
          <Avatar size="small" style={{ background: token.colorPrimary, fontSize: 11 }}>
            {(s.ownerName || s.principal).slice(0, 2).toUpperCase()}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ display: "block", fontSize: 13, lineHeight: 1.3 }}>{s.ownerName}</Text>
            {/* Truncated, not wrapped — a long email pushing the row to two
                lines threw off the row height (and the Popover/Tag baseline
                alignment) of every other column next to it. */}
            <Text
              type="secondary"
              style={{ fontSize: 11, fontFamily: MONO, display: "block" }}
              ellipsis={{ tooltip: s.ownerEmail }}
            >
              {s.ownerEmail}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      // "Sandbox" was redundant/ambiguous inside a table already titled
      // "Sandboxes" — every row is one. This column's real content is the
      // image (primary line) + workspace ID (secondary), so it's named for
      // what it actually shows, matching the image/FleetImage vocabulary
      // used elsewhere in this codebase.
      title: "Image",
      key: "sandbox",
      width: 220,
      render: (_: unknown, s: Sandbox) => (
        // minWidth: 0 — same fix as the Owner column above: a CSS grid/flex
        // item's default min-width: auto lets Text's ellipsis measurement
        // get skipped, so the image/workspace strings wrapped across
        // several lines instead of truncating with "…".
        <div style={{ minWidth: 0 }}>
          <Text style={{ display: "block", fontFamily: MONO, fontSize: 12 }} ellipsis={{ tooltip: s.image }}>{s.image}</Text>
          {s.workspaceId && (
            <Text type="secondary" style={{ display: "block", fontFamily: MONO, fontSize: 11 }} ellipsis={{ tooltip: s.workspaceId }}>{s.workspaceId}</Text>
          )}
          {s.egress && (
            <Text type="secondary" style={{ display: "block", fontSize: 11 }} ellipsis={{ tooltip: s.egress }}>{s.egress}</Text>
          )}
        </div>
      ),
    },
    {
      // One column, not two — Connected isn't an independent fact sitting
      // alongside Status, it's a qualifier OF "running" (see FleetPage's own
      // adapter: a live session forces status to "running"; idle/stopped can
      // never show a client). Nesting the connected indicator inside the
      // Status cell, only when it's actually running, encodes that
      // relationship directly instead of two same-weight columns that used
      // to both need a popover explaining how they relate to each other.
      title: "Status",
      key: "status",
      width: 220,
      render: (_: unknown, s: Sandbox) => (
        <Space size={6} align="center">
          <Tag color={STATUS_TAG_COLOR[s.status]} mono style={{ margin: 0 }}>{s.status}</Tag>
          {s.status === "running" && (
            <Popover
              content={
                <div style={{ maxWidth: 260 }}>
                  {s.sessions > 0
                    ? `${s.sessions} live session${s.sessions === 1 ? "" : "s"} from ${s.clients.join(", ")}`
                    : "No client attached — the sandbox is running but nobody is connected"}
                </div>
              }
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12,
                color: s.sessions > 0 ? token.colorSuccess : token.colorTextQuaternary }}>
                {s.sessions > 0 ? <Wifi size={13} /> : <WifiOff size={13} />}
                {/* mono only for the identifier (a client IP, or a raw session
                    count) — "not connected" is plain English, same as "Medium"
                    and "Enforcing Policy" elsewhere on this card, so it stays
                    sans rather than inheriting the row's default mono. */}
                {s.sessions > 0
                  ? <span style={{ fontFamily: MONO }}>{s.clients[0] || String(s.sessions)}</span>
                  : "not connected"}
              </span>
            </Popover>
          )}
        </Space>
      ),
    },
    {
      title: "Age",
      key: "age",
      width: 90,
      render: (_: unknown, s: Sandbox) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{s.ageLabel}</Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: unknown, s: Sandbox) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "stop",
                icon: <Square size={14} />,
                label: "Stop",
                // Only a running sandbox can be stopped — offering it on an
                // already-idle/stopped one implied an action that has
                // nothing to do.
                disabled: s.status !== "running",
                onClick: () => onAction(s.principal, "stop"),
              },
              { type: "divider" },
              {
                key: "destroy",
                danger: true,
                icon: <Trash2 size={14} />,
                label: "Destroy",
                onClick: () =>
                  Modal.confirm({
                    title: "Destroy this sandbox?",
                    content: "Removes the sandbox. The developer can re-launch it.",
                    okText: "Destroy",
                    okButtonProps: { danger: true },
                    onOk: () => onAction(s.principal, "destroy"),
                  }),
              },
            ],
          }}
        >
          <CustomButton size="small" type="text" icon={<MoreVertical size={15} />} aria-label={`Manage sandbox for ${s.ownerName || s.principal}`} />
        </Dropdown>
      ),
    },
  ];

  // No size="small" on the Card below — that drops AntD Card's body padding
  // to 12px; the user asked for the card's real default padding (24px) instead.
  return (
    <CustomCard>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {/* center, not flex-start — the left group (hostname/tag/uptime) and
            right group (badge/chips/kebab) mix a few different line-heights
            (a Title, plain Text, an antd Badge dot+text, Tag pills); at
            narrow widths, where the hostname ellipsizes and everything
            crowds onto one row, flex-start let those baselines drift out of
            line with each other instead of sharing a center. */}
        {/* flexWrap — the right-side group now carries two full CTA buttons
            (Invite via SSO, Create SSH Sandbox) alongside the
            kebab, not just the kebab alone; at narrower widths that group
            wraps onto its own row under the hostname rather than squeezing
            everything onto one line. rowGap covers the gap that only
            matters once it wraps. */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 8, flexWrap: "wrap", rowGap: 8 }}>
          {/* A plain flex row, not <Space> — <Space> wraps each child in its own
              flex item div, so flexShrink/minWidth set on the CHILDREN (as opposed
              to that wrapper) has no effect. A long real hostname would then force
              the Tag's own box to shrink and its text to wrap. Here the hostname
              is the one element that's allowed to shrink (and ellipsize); the role
              tag, info icon, and uptime are pinned via flexShrink: 0 on the actual
              flex items. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 auto" }}>
            {/* level={3} — real semantic markup, matching ManagementHostCard's
                Control Plane title: with the page's own title a real <h1> (see
                corenet's PageHeader) and the "Hosts" section heading below the
                KPI row a real <h2>, each host card's title is a sub-section of
                that <h2>, so <h3> is the correct outline depth. */}
            <Title
              level={3}
              style={{ margin: 0, fontFamily: MONO, fontSize: 14, fontWeight: 600, minWidth: 0 }}
              // AntD's own truncation-aware tooltip — only shows when the text is
              // actually clipped, unlike a plain Tooltip wrapper (which fired even
              // when the full hostname already fit, restating what was already visible).
              ellipsis={{ tooltip: host.name }}
            >
              {host.name}
            </Title>
            {/* No plain "Host" tag — every card in this list is a fleet
                worker (the control-plane host renders as ManagementHostCard
                instead), so a role tag here was pure chrome. */}
            {/* REAL enforcing — green dot only when actually enforcing; amber
                otherwise. The label names what the dot is a status of, rather
                than "Enforcing"/"Not enforcing" — the dot itself carries the
                on/off signal. Moved here (between the hostname and uptime)
                from the header's right side, where it used to sit paired with
                a PolicyChip — the chip's now shown only in the metadata row's
                own Policy column below, not duplicated in the header. */}
            {/* fontSize on the `text` node itself, not Badge's own `style` —
                antd's .ant-badge-status-text class sets an explicit 14px that
                a style prop on the outer wrapper can't override by
                inheritance (confirmed via computed style: wrapper resolved
                to 12px, the actual text span still measured 14px). An inline
                style on a node nested inside that span wins on specificity. */}
            <Badge status={host.enforcing ? "success" : "warning"}
              text={<span style={{ fontSize: 12 }}>Enforcing Policy</span>}
              style={{ flexShrink: 0, whiteSpace: "nowrap", marginLeft: 8 }} />
            {/* marginLeft on top of the row's own gap:8 — a bit more breathing
                room specifically before uptime, which now sits directly next
                to the hostname on worker cards since the plain "Host" tag
                that used to separate them is gone. */}
            <Text type="secondary" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 3, flexShrink: 0, whiteSpace: "nowrap", marginLeft: 4 }}>
              <Clock size={12} /> up {host.uptime}
            </Text>
          </div>
          <Space size={8} style={{ flexShrink: 0 }}>
            {/* Promoted out of the kebab into visible CTAs — these two are
                the actions an admin reaches for constantly (onboarding a
                new user), unlike Resize/Decommission below, which stay in
                the overflow menu as rarer, more consequential actions. */}
            {/* IdCard — the identity/enrollment framing fits better than
                ShieldCheck (already doing double duty inside the modal
                for the Enrollment URL card) or Terminal (a leftover from
                when this generated a raw CLI command, removed earlier).
                UserShield would be the more literal pairing against
                Grant SSH access's KeyRound, but it's lucide 1.27.0+ and
                this project pins lucide-react@^0.453.0 — not worth a
                version bump for one icon. */}
            <CustomButton icon={<IdCard size={14} />} onClick={onConnect}>Invite via SSO</CustomButton>
            <CustomButton icon={<KeyRound size={14} />} onClick={onDeploySshSandbox}>Create SSH Sandbox</CustomButton>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "manage-ssh-access",
                    icon: <KeyRound size={14} />,
                    label: "Manage SSH Access",
                    onClick: onManageSshAccess,
                  },
                  { type: "divider" },
                  {
                    key: "resize",
                    icon: <SquareDashed size={14} />,
                    label: "Resize Host",
                    onClick: () => onResize(host.name, host.instanceSize),
                  },
                  { type: "divider" },
                  {
                    key: "remove",
                    danger: true,
                    icon: <Trash2 size={14} />,
                    label: sandboxCount ? `Decommission (${sandboxCount} sandbox${sandboxCount > 1 ? "es" : ""})` : "Decommission host",
                    onClick: handleRemove,
                  },
                ],
              }}
            >
              <CustomButton size="small" type="text" icon={<MoreVertical size={15} />} aria-label={`Manage ${host.name}`} />
            </Dropdown>
          </Space>
        </div>

        {/* Full-width rule separating the card's identity row (name, role tag,
            enforcing status, management menu) from its metadata below — the
            header row reads as a distinct zone from the facts/metrics/table
            that follow it. */}
        <Divider style={{ margin: 0 }} />

        {/* Labeled fact grid, not a dot-joined line — cloud/region/instance/kernel/
            public IP are unrelated facts that scanned better one-per-cell than as
            one long mono sentence, especially with the kernel string (~40 chars)
            crowding everything after it. Enforced policies lives in the header row
            now (next to the Enforcing Policies badge it's the tiers for), not here.
            auto-fit + minmax floor (not a fixed repeat(5, ...) count) — at narrower
            widths, columns that can't fit at >=110px wrap onto their own row instead
            of compressing toward unreadable or forcing a horizontal scrollbar. */}
        {/* columnGap only (+50%), rowGap unchanged — "between-column padding"
            means horizontal spacing; wrapped-row spacing wasn't the ask. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", rowGap: token.padding, columnGap: token.padding * 1.5 }}>
          <div style={{ minWidth: 0 }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>Public IP / Dev Host</Text>
            {/* Blank (not a fallback value) is deliberate — this is the address
                handed out as "Dev host" when inviting a user via SSO, and an
                empty cell here surfaces a real gap (a worker with no external
                address yet) at a glance, instead of only inside that modal. */}
            {host.publicIp ? (
              <Text style={{ fontSize: 13, fontFamily: MONO, display: "block" }} ellipsis={{ tooltip: host.publicIp }} copyable={{ text: host.publicIp }}>
                {host.publicIp}
              </Text>
            ) : (
              <div style={{ fontSize: 13, color: token.colorTextTertiary }}>—</div>
            )}
          </div>
          <div>
            {/* Same PolicyChip + value as the "Enforcing Policy" badge in the
                header row above — this is a second, metadata-row placement of
                the same fact (not a separate/competing one), same reasoning
                as surfacing Public IP both places would follow. */}
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>Policy</Text>
            <div style={{ fontSize: 13 }}>
              {host.policyTier ? <PolicyChip tier={host.policyTier} /> : <span style={{ color: token.colorTextTertiary }}>—</span>}
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>Cloud</Text>
            <div style={{ fontSize: 13, color: token.colorText }}>{host.cloud}</div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>Region</Text>
            {/* mono, like Instance/Kernel — a raw provider region code ("eastus2"),
                not a descriptive word like Cloud's "Azure". */}
            <div style={{ fontSize: 13, fontFamily: MONO, color: token.colorText }}>{host.region}</div>
          </div>
          <div style={{ minWidth: 0 }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>Instance</Text>
            {/* flexWrap — at narrower viewports the raw SKU (e.g.
                "Standard_D4as_v7") plus the size tag can outgrow the cell;
                wrapping drops the tag to its own line instead of overflowing
                into the Kernel cell next door. */}
            <div style={{ fontSize: 13, fontFamily: MONO, color: token.colorText, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {host.instanceSize}
              {/* The size catalog's friendly Small/Medium/Large name (the same
                  vocabulary the Add-host/Resize picker uses) — grounds the raw
                  cloud SKU in a tier a reader can size up at a glance, and is
                  what the sandbox-capacity number above is actually keyed to.
                  No `mono` — "Medium" is a plain-English word, not an
                  identifier/slug (contrast the size tier with "citizen-dev"
                  below, or "Enforcing" above, which stays sans for the same
                  reason). */}
              {/* Default Tag sizing, not a shrunk-down variant — matches the
                  Status tag in the Sandboxes table below rather than reading
                  as a smaller, lesser-weight label. */}
              {sizeTier && (
                <Tag style={{ margin: 0 }}>
                  {sizeTier}
                </Tag>
              )}
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>Kernel</Text>
            {/* AntD's own truncation-aware tooltip, not a plain Tooltip wrapper — only
                shows on hover when the text is actually clipped (see the same fix on
                the card's own name/hostname above). copyable alongside it — this is
                the longest, most annoying-to-retype value on the card. */}
            <Text style={{ fontSize: 13, fontFamily: MONO, display: "block" }} ellipsis={{ tooltip: host.kernel }} copyable={{ text: host.kernel }}>
              {host.kernel}
            </Text>
          </div>
        </div>

        {/* columnGap matches the fact grid above exactly (token.padding * 1.5,
            not a hardcoded value) — with 3 equal 1fr tracks here against the
            fact grid's up-to-6 auto-fit tracks, the total-width math only
            works out to aligned boundaries (each metric column = 2 fact
            columns + 1 gap) when both grids use the identical gap. A
            hardcoded 36 here silently drifted out of sync with the fact
            grid's token-derived gap and threw off alignment whenever the
            fact grid actually rendered 6 columns. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: 24, columnGap: token.padding * 1.5 }}>
          <div>
            {/* No size modifier here — the Small/Medium/Large tag on the
                Instance field above already answers "why this many slots";
                repeating it here was redundant. */}
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Sandboxes {sandboxCount}/{host.sandboxCapacity}
            </Text>
            <Progress percent={host.sandboxCapacity ? (sandboxCount / host.sandboxCapacity) * 100 : 0}
              showInfo={false} size="small"
              strokeColor={sandboxCount >= host.sandboxCapacity ? token.colorError : token.colorPrimary} />
          </div>
          <div>
            {/* textTransform: uppercase is a blind per-character transform — it'd
                turn the intentionally-cased "vCPU" into "VCPU". Opt that one word
                back out with its own span. */}
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
              CPU {host.cpuPercent}% · {host.vcpu} <span style={{ textTransform: "none" }}>vCPU</span>
            </Text>
            <Progress percent={host.cpuPercent} showInfo={false} size="small" strokeColor={meter(host.cpuPercent)} />
          </div>
          <div>
            {/* Same fix as CPU's vCPU — GiB would otherwise become GIB. */}
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Memory {host.memPercent}% · {host.memGiB} <span style={{ textTransform: "none" }}>GiB</span>
            </Text>
            <Progress percent={host.memPercent} showInfo={false} size="small" strokeColor={meter(host.memPercent)} />
          </div>
        </div>

        {/* Collapsed by default — a card-per-host page with every sandbox
            table already expanded gets long fast. AntD's own Collapse
            (ghost, no border/background) gives the toggle/chevron/aria
            wiring for free instead of hand-rolling open-state + a11y. */}
        <Collapse
          ghost
          expandIconPosition="start"
          // overflowAnchor: none — the browser's scroll-anchoring heuristic
          // otherwise "corrects" the scroll position while this panel's
          // height animates open/closed, since it picks an anchor node
          // inside the very subtree that's resizing. That reads as the
          // page jumping out from under the cursor right after the click
          // that opened it. Opting this subtree out leaves the toggle
          // (and everything above it) exactly where the click landed.
          style={{ overflowAnchor: "none" }}
          styles={{ header: { padding: 0, alignItems: "center" }, body: { padding: "14px 0 0" } }}
          items={[{
            key: "sandboxes",
            label: (
              // A proper sub-heading, not the small uppercase fact-label style
              // used above — this introduces a whole table, not a single value,
              // so it needs more visual weight. Real <h3> (level={3}) for the
              // correct semantic level under the card's own <h2> title; AntD's
              // actual h3 default (24px) read too big for a compact card, so
              // it's sized down to 14px like the card titles above it. Sans,
              // not the app's default heading serif — this is a clickable
              // collapse toggle, not editorial copy, so it opts out of the
              // typography cascade the same way a control's label would.
              <Title level={3} style={{ fontFamily: SANS, fontSize: 14, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
                Sandboxes
                {/* A plain count, not a notification — neutral fill (not
                    antd's default red) so it doesn't read as an alert. */}
                <Badge
                  count={sandboxCount}
                  showZero
                  color={token.colorFillSecondary}
                  style={{ color: token.colorText, boxShadow: "none" }}
                />
              </Title>
            ),
            children: (
              <CustomTable<Sandbox>
                rowKey="id"
                dataSource={sandboxes}
                columns={columns}
                pagination={false}
                size="small"
                // Without this, antd falls back to the browser's default
                // table-layout: auto, which lets long content (Owner, with
                // no width of its own) force OTHER columns to shrink well
                // below their configured `width` — Age (width: 90) was
                // collapsing to a few px and wrapping "6 days" onto 3 lines
                // even with visible empty space in the table. tableLayout=
                // "fixed" makes every column's `width` strictly respected.
                tableLayout="fixed"
                locale={{ emptyText: `No sandboxes — ${host.sandboxCapacity} slots free on this host.` }}
              />
            ),
          }]}
        />
      </Space>
    </CustomCard>
  );
};

export default HostCard;
